import * as React from "react";
import { FormEvent, useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import { Theme } from "@mui/material/styles";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import IdentificationForm from "../../../components/forms/survey/IdentificationForm";
import { useApp } from "../../../hooks/useApp";
import { StartSurvey, SurveyType, SurveyInvitationType, TSurveysState } from '../../../types/Survey';
import { useDispatch, useSelector } from "react-redux";
import { IAppState } from "../../../store/Store";
import { TApiUserDepartment, TApiUserLocation } from "../../../types/User";
import { withSurveyData } from "../../../components/hoc/requiresSurveyData";
import { TDispatch } from "../../../types/Thunk";
import { startSurvey } from "../../../actions/SurveyActions";
import { RouteComponentProps, withRouter } from "react-router";
import { compose } from "redux";
import { reverse } from "named-urls";
import { routes } from "../../../Routes";
import { useCurrentActiveSurvey } from "../../../hooks/useCurrentActiveSurvey";
import SurveyTranslation from "../../../components/survey/SurveyTranslation";
import { StaticContext } from "react-router";
import { determineSelectableDepLoc } from '../../../helpers/Survey';
import { TAuthState } from '../../../types/Auth';

type LocationState = {
  language: string;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    heading: {
      fontSize: theme.typography.pxToRem(24),
      textAlign: "center"
    }
  })
);

const SurveyIdentificationPage: React.FC<RouteComponentProps<
  any,
  StaticContext,
  LocationState
>> = (props): JSX.Element => {
  const classes = useStyles({});
  const { app } = useApp();
  const dispatch = useDispatch<TDispatch>();
  const surveysState: TSurveysState = useSelector<IAppState, TSurveysState>(
    state => state.surveysState
  );

  const auth: TAuthState = useSelector<IAppState, TAuthState>(
    (state: IAppState) => state.authState
  );

  const [error, setError] = useState(false);
  const { setRespondent, addToRespondentsStorage } = useCurrentActiveSurvey();

  const [name, setName] = React.useState("");

  const [employeeIdentification, setEmployeeIdentification] = React.useState<boolean>(false);
  const [selectableDepartments, setSelectableDepartments] = React.useState<Array<TApiUserDepartment>>([]);
  const [selectableLocations, setSelectableLocations] = React.useState<Array<TApiUserLocation>>([]);

  const [organization, setOrganization] = React.useState<TApiUserDepartment>({
    id: 0,
    title: "",
    children: []
  });
  const [location, setLocation] = React.useState<TApiUserLocation>({
    id: 0,
    title: "",
    children: []
  });

  const goToCategory = () => {
    props.history.push(
      reverse(routes.surveyCategory, {
        id: surveysState.currentSurvey?.survey.id as number
      })
    );
  };

  useEffect(() => {
    const currentSurvey = surveysState.currentSurvey;
    const currentSurveyIdentification = surveysState.currentSurveyIdentification;

    setEmployeeIdentification(currentSurveyIdentification?.employeeIdentification ?? false);
    let selectableDepartments = currentSurveyIdentification?.departments ?? [];
    let selectableLocations = currentSurveyIdentification?.locations ?? [];

    if (currentSurvey?.survey.anonymity === SurveyType.REQUIRES_LOGIN) {
      const departments = auth.data?.user?.departments;
      const locations = auth.data?.user?.locations;

      selectableDepartments = determineSelectableDepLoc(selectableDepartments, departments);
      selectableLocations = determineSelectableDepLoc(selectableLocations, locations);
      setName(auth.data?.user?.name ?? '');
    }

    setSelectableDepartments(selectableDepartments);
    setSelectableLocations(selectableLocations);
  }, [auth.data, surveysState.currentSurvey, surveysState.currentSurveyIdentification]);

  useEffect(() => {
    const firstLocation = selectableLocations[0];
    if (firstLocation) {
      setLocation(firstLocation);
    }

    const firstDepartment = selectableDepartments[0];
    if (firstDepartment) {
      setOrganization(firstDepartment);
    }
  }, [selectableDepartments, selectableLocations]);

  const survey = surveysState.currentSurvey?.survey;
  const isGatedIdentification =
    survey?.anonymity === SurveyType.REQUIRES_LOGIN &&
    survey?.invitationType === SurveyInvitationType.MANUAL;
  const departmentReadOnly = isGatedIdentification && selectableDepartments.length === 1;
  const locationReadOnly = isGatedIdentification && selectableLocations.length === 1;

  const submitIdentificationForm = (
    e: FormEvent,
    anonymous: boolean = false
  ) => {
    e.preventDefault();
    dispatch(
      startSurvey({
        surveyId: surveysState.currentSurvey!.survey.id,
        languageCode: props.location.state.language,
        respondentName: name,
        departmentId: organization?.id,
        locationId: location?.id
      })
    )
      .then((res) => {
        setRespondent(
          res.data.respondent,
          surveysState.currentSurvey!.survey.id,
          props.location.state.language
        );
        if (surveysState.currentSurvey?.survey.allowSavingProgress) {
          addToRespondentsStorage(
            surveysState.currentSurvey?.survey.id,
            res.data.respondent
          );
        }
        goToCategory();
      })
      .catch(() => {
        setError(true);
      });
  };

  return (
    <>
      <Typography className={classes.heading}>
        <SurveyTranslation
          translate={"survey.identification.before.begin.title"}
        />
      </Typography>
      <p>
        {isGatedIdentification ? (
          survey?.verificationMessage !== null &&
          survey?.verificationMessage !== undefined ? (
            <>{survey.verificationMessage}</>
          ) : (
            <SurveyTranslation
              translate={"survey.identification.verification_message"}
            />
          )
        ) : (
          <SurveyTranslation
            translate={"survey.identification.before.begin.description"}
          />
        )}
      </p>
      <IdentificationForm
        name={name}
        setName={setName}
        selectableLocations={selectableLocations}
        location={location}
        setLocation={setLocation}
        selectableDepartments={selectableDepartments}
        organization={organization}
        setOrganization={setOrganization}
        submitForm={submitIdentificationForm}
        survey={surveysState}
        errors={error && app.getError(StartSurvey)}
        employeeIdentification={employeeIdentification}
        departmentReadOnly={departmentReadOnly}
        locationReadOnly={locationReadOnly}
        showReadOnlyName={isGatedIdentification}
        segmentationContinueButton={surveysState.currentSurvey?.survey.segmentationContinueButton ?? null}
        segmentationId={surveysState.currentSurvey?.survey.segmentationId ?? null}
        selectSegmentationLocation={surveysState.currentSurvey?.survey.selectSegmentationLocation ?? null}
        selectSegmentationOrganisation={surveysState.currentSurvey?.survey.selectSegmentationOrganisation ?? null}
      />
    </>
  );
};
export default compose(
  withSurveyData,
  withRouter
)(SurveyIdentificationPage) as React.FC;
